import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxSubTabs from "../../../../../components/SbuxTabs/SbuxSubTabs";
import TemporaryMsgTimePicker from "./components/TemporaryMsgTimePicker";
import TemporaryAudioConfig from "../../common/TemporaryAudioConfig";
import TemporaryMsgMode from "./components/TemporaryMsgMode";
import TemporaryMsgDateTimeOption from "./components/TemporaryMsgTimeOption";
import { Box, Divider, FormControl, InputLabel } from "@mui/material";
import dayjs from "../../../../../utils/dayjs";
import { useAppSelector } from "../../../../../store/types";
import styles from "../../styles";
import useCss from "../../../../../hooks/useCss";

import { getGlobalConfigUiSelector } from "../../../../../selectors/globalConfigSelectors";
import { getLanguagesSelector } from "../../../../../selectors/userInstanceSelector";
import { getMediaMgtDataSelector } from "../../../../../selectors/mediaMgtSelector";
import SbuxTranslate from "../../../../../components/SbuxTranslate";

// Status values that indicate the record is locked (not editable)
const LOCKED_STATUSES = ["Expired", "Disabled", "Active", "Scheduled"] as const;

/** Check if record status prevents editing description/info fields */
const isStatusLocked = (status: unknown): boolean =>
  LOCKED_STATUSES.includes(status as (typeof LOCKED_STATUSES)[number]);

/** Calculate the next 30-minute time slot */
const getNextTimeSlot = () => {
  const remainder = 30 - (dayjs().minute() % 30);
  const startTime = dayjs().add(remainder, "minutes").format("hh:mm A");
  const endTime = dayjs(startTime, "hh:mm A")
    .add(30, "minutes")
    .format("hh:mm A");
  return { startTime, endTime };
};

interface TemporaryMsgEditProps {
  row: Record<string, unknown>;
  handleTemporaryMsgRow: (
    row: Record<string, unknown>,
    isAudioFileChanged?: boolean,
    isTimeChanged?: boolean,
  ) => void;
}

type GlobalConfigUi = Record<
  string,
  Array<{ value: string; [key: string]: unknown }>
>;
type LanguageItem = { code: string; name: string; [key: string]: unknown };

const TemporaryMsgEdit = ({
  row,
  handleTemporaryMsgRow,
}: TemporaryMsgEditProps) => {
  const classes = useCss(styles);
  const globalConfigUi = useAppSelector(
    getGlobalConfigUiSelector,
  ) as unknown as GlobalConfigUi | undefined;
  const languages = useAppSelector(getLanguagesSelector) as
    | LanguageItem[]
    | undefined;
  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);

  const [rowObj, setRowObj] = useState(row);

  const isLocked = isStatusLocked(row?.status);

  // Compute default times once at mount — shared by useEffect and initial useState values
  // to guarantee both see the same 30-minute slot even across a boundary.
  const defaultTimesRef = useRef(getNextTimeSlot());
  const { startTime: defaultStartTime, endTime: defaultEndTime } =
    defaultTimesRef.current;

  useEffect(() => {
    const defaultTimezone = globalConfigUi?.timezones?.[3]?.value ?? "";
    const initialObj = {
      mode: row.status === "Draft" ? "draftMode" : "durationMode",
      startTimeOption: row["startTime"] ? "userDefined" : "immediately",
      endTimeOption: row["endTime"] ? "userDefined" : "immediately",
      startTime: row["startTime"] ?? defaultStartTime,
      endTime: row["endTime"] ?? defaultEndTime,
      endDate: row["endDate"] ?? new Date(),
      startTimeZone: row.startTimeZone ?? defaultTimezone,
      endTimeZone: row.endTimeZone ?? defaultTimezone,
    };
    setRowObj({ ...initialObj, ...row });
    // globalConfigUi intentionally excluded from deps: it is only used for the one-time
    // timezone default, already captured in defaultTimesRef at mount. Adding it would
    // cause the effect to re-run when config resolves asynchronously and silently reset
    // any user edits already in progress. row changes are the correct trigger.
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const [description, setDescription] = useState<string>(
    (rowObj.description as string) ?? "",
  );
  const [descriptionError, setDescriptionError] = useState(false);
  const [mode, setMode] = useState(
    rowObj.status === "Draft" ? "draftMode" : "durationMode",
  );
  const [startTimeOption, setStartTimeOption] = useState(
    rowObj["startTime"] ? "userDefined" : "immediately",
  );
  const [endTimeOption, setEndTimeOption] = useState(
    rowObj["endTime"] ? "userDefined" : "immediately",
  );
  const [startDate, setStartDate] = useState<Date>(
    rowObj["startDate"]
      ? dayjs(rowObj.startDate as string).toDate()
      : new Date(),
  );
  const [endDate, setEndDate] = useState<Date>(
    rowObj["endDate"] ? dayjs(rowObj.endDate as string).toDate() : new Date(),
  );

  const [startTime, setStartTime] = useState<string>(
    rowObj["startTime"] ? (rowObj.startTime as string) : defaultStartTime,
  );
  const [endTime, setEndTime] = useState<string>(
    rowObj["endTime"] ? (rowObj.endTime as string) : defaultEndTime,
  );
  const [startTimeZone, setStartTimeZone] = useState<string>(
    rowObj["startTimeZone"]
      ? (rowObj.startTimeZone as string)
      : (globalConfigUi?.timezones?.[3]?.value ?? ""),
  );
  const [endTimeZone, setEndTimeZone] = useState(
    globalConfigUi?.timezones?.[3]?.value ?? "",
  );

  const [languageTabId, setLanguageTabId] = useState(
    languages?.[0]?.code ?? "",
  );
  const [languageMessage, setLanguageMessage] = useState<
    Record<string, unknown>
  >({});

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setDescription(value);
    setDescriptionError(value.length < 1 ? true : false);
    const updateRow = {
      ...rowObj,
      description: value,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow);
  };

  const handleInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const updateRow = {
      ...rowObj,
      info: value,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow);
  };

  const handleDateChange = (value: { date: Date | null; name: string }) => {
    let updateRow: Record<string, unknown> = { ...rowObj };
    if (value.name === "Start Time") {
      if (value.date) setStartDate(value.date);
      updateRow = { ...rowObj, startDate: value.date };
    } else if (value.name === "End Time") {
      if (value.date) setEndDate(value.date);
      updateRow = { ...rowObj, endDate: value.date };
    }
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, false, true);
  };
  const handleStartTimeChange = (value: { time: string; name: string }) => {
    setStartTime(value.time);
    let addRow: Record<string, unknown> = {
      ...rowObj,
      startTime: value.time,
      ...languageMessage,
    };
    const timeString = value.time;
    const [time, modifier] = timeString.split(" ");
    const timeParts = time.split(":");
    let hoursNum = parseInt(timeParts[0], 10);
    const minutesNum = parseInt(timeParts[1], 10);
    if (modifier === "PM" && hoursNum < 12) {
      hoursNum += 12;
    }
    const sDate = dayjs(startDate).format("MM/DD/YYYY");
    const eDate = dayjs(endDate).format("MM/DD/YYYY");
    const dateDiff = sDate === eDate ? 0 : 1;

    if (hoursNum >= 23 && minutesNum >= 30 && !dateDiff) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setEndDate(tomorrow);
      setEndTime("12:00 AM");
      addRow = {
        ...addRow,
        endDate: tomorrow,
        endTime: "12:00 AM",
      };
    }
    setRowObj(addRow);
    handleTemporaryMsgRow(addRow, false, true);
  };
  const handleEndTimeChange = (value: { time: string; name: string }) => {
    setEndTime(value.time as string);
    const updateRow: Record<string, unknown> = {
      ...rowObj,
      startTime: rowObj.startTime,
      endTime: value.time,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, false, true);
  };
  const handleTimeZoneChange = (timeZone: string) => {
    setStartTimeZone(timeZone);
    setEndTimeZone(timeZone);
    const updateRow: Record<string, unknown> = {
      ...rowObj,
      startTimeZone: timeZone,
      endTimeZone: timeZone,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, false, true);
  };

  const handleModeValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setMode(event.target.value);
    if (event.target.value === "draftMode") {
      setStartTimeOption("immediately");
      setEndTimeOption("immediately");
    }
    const updateRow = {
      ...rowObj,
      mode: event.target.value,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, false, true);
  };

  const handleStartTimeOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStartTimeOption(event.target.value);
    const startDateVal = rowObj["startDate"]
      ? dayjs(rowObj.startDate as string).toDate()
      : new Date();
    const updateRow = {
      ...rowObj,
      startDate: startDateVal,
      startTimeOption: event.target.value,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, false, true);
  };

  const handleEndTimeOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const endDateVal = rowObj["endDate"]
      ? dayjs(rowObj.endDate as string).toDate()
      : new Date();
    setEndDate(endDateVal);
    setEndTimeOption(event.target.value);
    const updateRow = {
      ...rowObj,
      endDate: endDateVal,
      endTimeOption: event.target.value,
    };
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, false, true);
  };

  const handleLanguageMediaFile = (
    value: string | null,
    isAudioFileChanged = false,
  ) => {
    let messageLanguageTabId = `message_${languageTabId}`;
    const existingLanguages =
      Object.keys(rowObj).length > 0 &&
      Object.keys(rowObj)
        .filter((a) => a.includes("message"))
        .reduce(
          (acc: Record<string, unknown>, cur) => (
            (acc[cur] = rowObj[cur]),
            acc
          ),
          {},
        );
    let updateLanguageMessage = Object.assign({}, languageMessage);
    setLanguageMessage(updateLanguageMessage);
    let updateRow = {
      ...rowObj,
    };
    if (value) {
      const mediaData = (
        mediaMgtData as import("../../../../../slices/mediaMgtSlice").MediaMgtDataContainer
      ).data;
      const mediaInfo = Array.isArray(mediaData)
        ? mediaData.find((m) => m.filePath === value)
        : undefined;

      // Check if mediaInfo and mediaInfo.filePath exist
      if (mediaInfo && mediaInfo.filePath) {
        const newLanguageMessage = {
          [`${messageLanguageTabId}`]: {
            type: "AUDIO",
            value: mediaInfo.filePath,
            accessType: mediaInfo.type,
          },
        };

        updateLanguageMessage = {
          ...existingLanguages,
          ...languageMessage,
          ...newLanguageMessage,
        };
        updateRow = {
          ...rowObj,
          ...updateLanguageMessage,
          languageMessage: { ...updateLanguageMessage },
          isAudioFileChanged: isAudioFileChanged,
        };
      } else {
        return;
      }
    } else {
      if (updateLanguageMessage[messageLanguageTabId]) {
        delete updateLanguageMessage[messageLanguageTabId];
      }
      updateRow = {
        ...rowObj,
        ...updateLanguageMessage, //This is required for button enabled or disabled validation
        languageMessage: { ...updateLanguageMessage }, // This is required adding new closure
        isAudioFileChanged: isAudioFileChanged,
      };
      if (updateRow[messageLanguageTabId]) {
        delete updateRow[messageLanguageTabId];
      }
    }
    setRowObj(updateRow);
    handleTemporaryMsgRow(updateRow, isAudioFileChanged);
  };

  // Memoize the language tabs to prevent unnecessary re-renders
  const languageTabs = useMemo(() => {
    return (languages ?? []).map((lang) => ({
      name: lang.name,
      value: lang.code,
    }));
  }, [languages]);

  const handleSubTabClick = useCallback((tab: { value: string }) => {
    setLanguageTabId(tab.value);
  }, []);

  return (
    <Box>
      <Box
        component="form"
        className={`${classes.formControl}`}
        noValidate
        autoComplete="off"
      >
        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Code"}</SbuxTranslate>}
            disabled
            value={rowObj.sequenceNumber as string | undefined}
            className={classes.disabledInput}
          />
        </Box>

        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Description"}</SbuxTranslate>}
            // placeholder={descriptionPlaceHolder}
            value={description}
            handleChange={handleDescriptionChange}
            errorMessage={
              <SbuxTranslate>
                {"Description required at least 1 character"}
              </SbuxTranslate>
            }
            isError={descriptionError}
            error={descriptionError}
            disabled={isLocked}
            className={isLocked ? classes.disabledInput : undefined}
          />
        </Box>

        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Info"}</SbuxTranslate>}
            value={rowObj.info as string | undefined}
            multiline={true}
            handleChange={handleInfoChange}
            disabled={isLocked}
            className={isLocked ? classes.disabledInput : undefined}
          />
        </Box>

        <Box>
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"Date Time"}</SbuxTranslate>
            </InputLabel>

            <TemporaryMsgMode
              row={row}
              mode={mode}
              handleModeValueChange={handleModeValueChange}
            />
          </FormControl>
        </Box>

        {mode === "durationMode" && (
          <>
            <Box>
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"Date Time"}</SbuxTranslate>
                </InputLabel>

                <TemporaryMsgDateTimeOption
                  name="Start Time"
                  timeOption={startTimeOption}
                  handleTimeOptionValueChange={handleStartTimeOptionChange}
                />
              </FormControl>
            </Box>

            {startTimeOption !== "immediately" && (
              <Box className="fullWidth">
                <TemporaryMsgTimePicker
                  type={1}
                  startDate={startDate}
                  endDate={endDate}
                  startTimeValue={startTime}
                  endTimeValue={endTime}
                  timeZoneValue={startTimeZone}
                  timeZoneList={globalConfigUi?.timezones ?? []}
                  startTimeOption={startTimeOption}
                  endTimeOption={endTimeOption}
                  handleDateChange={handleDateChange}
                  handleStartTimeChange={handleStartTimeChange}
                  handleTimeZoneChange={handleTimeZoneChange}
                  row={row}
                />
              </Box>
            )}

            <Box className="fullWidth">
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"End Time"}</SbuxTranslate>
                </InputLabel>

                <Box>
                  <TemporaryMsgDateTimeOption
                    name="End Time"
                    timeOption={endTimeOption}
                    handleTimeOptionValueChange={handleEndTimeOptionChange}
                  />
                </Box>
              </FormControl>
            </Box>

            {endTimeOption !== "immediately" && (
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"End Time"}</SbuxTranslate>
                </InputLabel>

                <Box className={classes.timePicker}>
                  <Box>
                    <TemporaryMsgTimePicker
                      type={2}
                      startDate={startDate}
                      endDate={endDate}
                      startTimeValue={startTime}
                      endTimeValue={endTime}
                      timeZoneValue={endTimeZone}
                      timeZoneList={globalConfigUi?.timezones ?? []}
                      startTimeOption={startTimeOption}
                      endTimeOption={endTimeOption}
                      handleDateChange={handleDateChange}
                      handleEndTimeChange={handleEndTimeChange}
                      handleTimeZoneChange={handleTimeZoneChange}
                      row={row}
                    />
                  </Box>
                </Box>
              </FormControl>
            )}

            <Divider />

            <Box className="fullWidth">
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"Audio Configuration"}</SbuxTranslate>
                </InputLabel>

                <Box>
                  <SbuxSubTabs
                    tabList={languageTabs}
                    tabId={languageTabId}
                    handleTabClick={handleSubTabClick}
                    width={"100%"}
                  />
                  <Box className={classes.tabContainer}>
                    <TemporaryAudioConfig
                      languageId={languageTabId}
                      handleLanguageMediaFile={handleLanguageMediaFile}
                      row={rowObj}
                      status={row?.status as string}
                    />
                  </Box>
                </Box>
              </FormControl>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TemporaryMsgEdit;
